<template>
  <content-container :loadingStatus="isLoading" style="min-width: 905px">
    <div class="contract-list content-wrapper">
      <div class="page-header">{{ $t('contractList') }}</div>
      <div>
        <el-row>
          <el-col :span="24">
            <contract-create-button-component
              :hotel-list="hotelList"
              :agent-list="agentList"
              :sale-person-list="salePersonList"
              @onCreateContract="onCreateContract">
            </contract-create-button-component>
          </el-col>
        </el-row>
        <contract-search-component
          :hotel-list="hotelList"
          :agent-list="agentList"
          :market-list="marketList"
          :sale-person-list="salePersonList"
          @onSearchButtonClick="onSearchButtonClick"
          @onResetButtonClick="onResetButtonClick">
        </contract-search-component>
        <div style="margin-top: -10px;">
          <span style="margin-right: 10px">Record per pages</span>
          <el-select v-model="itemPerPage" placeholder="Select" @change="onItemPerPageChange">
            <el-option
              v-for="item in pageSizeOptions"
              :key="`item-per-page-${item.label}`"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </div>
        <contract-list-component
          v-model="contractList"
          :hotel-list="hotelList"
          :agent-list="agentList"
          :totalListLength="totalListLength"
          :itemPerPage="itemPerPage"
          :market-list="marketList"
          :sale-person-list="salePersonList"
          :disabled-next="currentPage === maxPage"
          :disabled-previous="currentPage === 1"
          :current-page="currentPage"
          @onRemovedContract="onRemoveContract"
          @onDownloadButtonClick="onDownloadButtonClick"
          @onPreviewButtonClick="onPreviewButtonClick"
          @onOpenPopupSendEmailButtonClick="onOpenPopupSendEmailButtonClick"
          @onDuplicateButtonClick="onDuplicateButtonClick"
          @onNextButtonClick="onNextButtonClick"
          @onPageChange="onPageChange"
          @onPreviousButtonClick="onPreviousButtonClick"
        >
        </contract-list-component>
      </div>
    </div>

    <preview-contract
      :visible="isShowPreview"
      :hotel="downloadHotelData"
      :contract-detail="downloadContractDetail"
      :supplement-type-list="downloadSupplementTypeList"
      :company-data="downloadCompanyData"
      @onClose="isShowPreview = false"
      @onDownloadStart="startLoading"
      @onDownloadSuccess="stopLoading"
      @onDownloadError="onDownloadError"
      @onSaveDraft="onPreviewSaveDraft"
      @onSavePublish="onPreviewSavePublish"
    ></preview-contract>

    <popup-send-email-component
      :title="$t('sendEmailContract')"
      :visible="isPopupSendEmailVisible"
      :init-data="initEmailData"
      :send-type="EMAIL_SEND_TYPE.CONTRACT"
      :ref-code="contractDetail.refCode"
      :agent="contractDetail.agent"
      @onClose="isPopupSendEmailVisible = false">
    </popup-send-email-component>
  </content-container>
</template>

<script>
import i18nMessages from '@/translations/Contract/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import AgentService from '@/services/agent-service'
import ContractApi from '@/api/contract-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import PopupSendEmailComponent from '@/components/PopupSendEmailComponent'
import { get } from 'lodash'
import * as html2Pdf from 'html2pdf.js'
import { forceDownloadFileUrl, delay } from '@/helpers'
import { dateFormat } from '@/helpers/datetime'
import { getContractFileName } from '@/domains/contract-domain'
import { prepareDocumentData, updateContractStatus, getDuplicateContractDetail } from '@/services/contract-service'
import { CONTRACT_STATUS, STATUS, DOCUMENT_TYPE, VUEX_EVENT, EMAIL_SEND_TYPE } from '@/configs/constant'
import ContractCreateButtonComponent from '@/views/Contract/components/ContractCreateButtonComponent'
import ContractSearchComponent from '@/views/Contract/components/ContractSearchComponent'
import ContractListComponent from '@/views/Contract/components/ContractListComponent'
import PreviewContract from '@/views/Contract/components/ManageContract/PreviewContract'
import { mapState } from 'vuex'
import { getHotelInfo } from '@/services/hotel-service'

export default {
  name: 'ContractList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: {
    PopupSendEmailComponent,
    PreviewContract,
    ContentContainer,
    ContractCreateButtonComponent,
    ContractSearchComponent,
    ContractListComponent
  },
  i18n: { messages: i18nMessages },
  data () {
    return {
      EMAIL_SEND_TYPE,
      CONTRACT_STATUS,
      isLoading: false,
      isShowPreview: false,
      contractList: [],
      contractPageList: {},
      lastId: '',
      currentPage: 1,
      maxPage: 2,
      agentList: [],
      marketList: [],
      hotelList: [],
      salePersonList: [],
      filter: {
        refCode: '',
        hotelId: '',
        agentId: '',
        agentIdList: [],
        contractValid: '',
        salePersonId: '',
        status: '',
        marketId: ''
      },
      downloadContractDetail: {
        id: '',
        hotelId: '',
        refCode: '',
        startDate: '',
        endDate: '',
        salePerson: {},
        internalNote: '',
        agent: {},
        status: CONTRACT_STATUS.DRAFT,
        rateCodeList: [],
        allotmentList: [],
        cutOffDateList: [],
        promotionList: [],
        supplementList: [],
        transferList: [],
        termsOfPaymentList: [],
        remarkList: [],
        termAndCondition: {
          updatedAt: '',
          dataList: []
        }
      },
      downloadSupplementTypeList: [],
      downloadCompanyData: {
        id: '',
        name: '',
        code: '',
        companyType: 1,
        currency: 'THB',
        isEnable: STATUS.ENABLE
      },
      downloadHotelData: {
        companyId: '',
        id: '',
        name: '',
        remark: '',
        information: {},
        isEnable: STATUS.ENABLE
      },
      contractDetail: {},
      initEmailData: {
        senderName: '',
        senderNameList: [],
        replyEmail: '',
        toEmailList: [],
        ccEmailList: [],
        bccEmailList: [],
        attachmentList: []
      },
      isPopupSendEmailVisible: false,
      itemPerPage: 10,
      pageSizeOptions: [{
        value: 10,
        label: '10'
      }, {
        value: 20,
        label: '20'
      }, {
        value: 50,
        label: '50'
      }, {
        value: 100,
        label: '100'
      }, {
        value: 999,
        label: 'All'
      }],
      totalListLength: 1
    }
  },
  computed: {
    ...mapState({
      contractSearchCriteria: state => state.contract.contractSearchCriteria,
      contractListCurrentPage: state => state.contract.contractListCurrentPage,
      contractListLastId: state => state.contract.contractListLastId,
      contractPageListStore: state => state.contract.contractPageListStore
    })
  },
  created: async function () {
    await this.initDataList()
  },
  methods: {
    initDataList: async function () {
      this.isLoading = true
      try {
        this.filter = Object.assign(this.filter, this.contractSearchCriteria)

        const data = {
          filter: this.filter,
          lastId: (this.contractListCurrentPage > 1) ? this.contractListLastId : '',
          isFirstLoad: true,
          isContractPage: true,
          length: this.contractList.length * (this.currentPage - 1),
          limitItem: this.itemPerPage
        }

        const result = await Promise.all([
          AgentService.getAgentList(),
          ContractApi.list(data)
        ])

        this.agentList = result[0]
        const contractListResponse = result[1]

        this.hotelList = contractListResponse.hotelList
        this.marketList = contractListResponse.marketList
        this.totalListLength = contractListResponse.count
        this.pageSizeOptions[4].value = contractListResponse.count
        this.salePersonList = contractListResponse.salePersonList
        this.hotelList = this.hotelList.filter(hotel => hotel.isEnable === STATUS.ENABLE)

        const contractListLength = contractListResponse.contractList.length
        if (contractListResponse.contractList.length > 0) {
          this.lastId = contractListResponse.contractList[contractListLength - 1].id
        }

        this.contractList = [...contractListResponse.contractList]
        this.contractPageList[this.currentPage] = [...contractListResponse.contractList]

        if (this.contractListCurrentPage > 1) {
          this.currentPage = this.contractListCurrentPage

          this.contractPageList = Object.assign(this.contractPageList, this.contractPageListStore)
          this.contractList = [...this.contractPageList[this.currentPage]]

          this.lastId = this.contractList[this.contractList.length - 1].id
        }

        this.maxPage = (contractListResponse.moreItem) ? this.currentPage + 1 : this.currentPage
        this.stopLoading()
      } catch (err) {
        this.alertError(err.message)
      }
      this.isLoading = false
    },
    getContractList: async function () {
      this.startLoading()

      try {
        const data = {
          filter: this.filter,
          lastId: this.lastId,
          isFirstLoad: false,
          isContractPage: true
        }
        const contractListResponse = await ContractApi.list(data)
        const contractListLength = contractListResponse.contractList.length
        this.$store.dispatch(VUEX_EVENT.CONTRACT.SET_CONTRACT_LIST_LAST_ID, this.lastId)

        if (contractListLength > 0) {
          this.lastId = contractListResponse.contractList[contractListLength - 1].id
        }

        this.contractList = contractListResponse.contractList
        this.totalListLength = contractListResponse.count
        this.contractPageList[this.currentPage] = contractListResponse.contractList

        this.maxPage = (contractListResponse.moreItem) ? this.currentPage + 1 : this.currentPage
      } catch (err) {
        this.alertError(err.message)
      }
      this.stopLoading()
    },
    onCreateContract: function (hotelId) {
      const hotelData = this.hotelList.find(hotel => hotel.id === hotelId)

      return this.$router.push({
        name: 'ContractAdd',
        params: {
          hotel: hotelData,
          agentList: this.agentList,
          salePersonList: this.salePersonList
        }
      })
    },
    onSearchButtonClick: async function (filter) {
      this.filter = Object.assign({}, filter)
      this.lastId = ''
      this.contractPageList = {}
      this.currentPage = 1
      await this.getContractList()
    },
    onResetButtonClick: async function (filter) {
      this.filter = Object.assign({}, filter)
      this.lastId = ''
      this.contractPageList = {}
      this.currentPage = 1
      await this.getContractList()
    },
    onItemPerPageChange: async function (itemPerPage) {
      this.currentPage = 1
      await this.initDataList()
    },
    onNextButtonClick: async function () {
      this.currentPage++

      if (this.contractPageList[this.currentPage] === undefined) {
        await this.getContractList()
      } else {
        this.startLoading()
        await delay(500)
        this.contractList = [...this.contractPageList[this.currentPage]]
        this.stopLoading()
      }

      this.$store.dispatch(VUEX_EVENT.CONTRACT.SET_CONTRACT_LIST_CURRENT_PAGE, this.currentPage)
      this.$store.dispatch(VUEX_EVENT.CONTRACT.SET_CONTRACT_PAGE_LIST_STORE, this.contractPageList)
    },
    onPreviousButtonClick: async function () {
      this.startLoading()
      await delay(500)

      if (this.currentPage < 2) {
        return false
      }

      this.currentPage--
      this.contractList = [...this.contractPageList[this.currentPage]]

      this.$store.dispatch(VUEX_EVENT.CONTRACT.SET_CONTRACT_LIST_CURRENT_PAGE, this.currentPage)
      this.stopLoading()
    },
    onRemoveContract: async function (contractId) {
      this.contractList = this.contractList.filter(item => item.id !== contractId)
    },
    onDownloadButtonClick: async function (contract) {
      try {
        this.startLoading()

        let fileType = contract.fileType
        let contractId = contract.contractId

        await this._getContractDetail(contractId)
        await delay(1500)

        const data = prepareDocumentData(this.downloadContractDetail, fileType)
        const refCode = this.downloadContractDetail.refCode.toLowerCase()

        let documentType = DOCUMENT_TYPE
        let fileName = ''
        let documentResult = ''

        if (fileType === 'pdf') {
          documentType = 'pdf'
          fileName = getContractFileName(refCode, 'contract-', 'pdf')
          await this._getContractPdfBase64Data(contractId, data, documentType, fileName, true)
        } else {
          documentType = DOCUMENT_TYPE
          fileName = getContractFileName(refCode)
          documentResult = await ContractApi.generateDocument(contractId, data, DOCUMENT_TYPE)

          const documentData = `data:${documentType}; base64, ${documentResult}`

          forceDownloadFileUrl(documentData, fileName)
          this._resetDownloadData()
        }
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _resetDownloadData: function () {
      this.downloadContractDetail = {
        id: '',
        hotelId: '',
        refCode: '',
        startDate: '',
        endDate: '',
        salePerson: {},
        internalNote: '',
        agent: {},
        status: CONTRACT_STATUS.DRAFT,
        rateCodeList: [],
        allotmentList: [],
        cutOffDateList: [],
        promotionList: [],
        supplementList: [],
        transferList: [],
        termsOfPaymentList: [],
        remarkList: [],
        termAndCondition: {
          updatedAt: '',
          dataList: []
        }
      }
      this.downloadCompanyData = {
        id: '',
        name: '',
        code: '',
        companyType: 1,
        currency: 'THB',
        isEnable: STATUS.ENABLE
      }
      this.downloadHotelData = {
        companyId: '',
        id: '',
        name: '',
        remark: '',
        information: {},
        isEnable: STATUS.ENABLE
      }
      this.downloadSupplementTypeList = []
    },
    onPreviewButtonClick: async function (contractId) {
      try {
        this.startLoading()

        await this._getContractDetail(contractId)
        this.isShowPreview = true
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onDuplicateButtonClick: async function (contractId) {
      try {
        this.startLoading()

        const duplicatedContractDetail = await getDuplicateContractDetail(contractId)
        const hotelData = this.hotelList.find(hotel => hotel.id === duplicatedContractDetail.hotelId)

        this.$router.push({
          name: 'ContractAdd',
          params: {
            duplicatedContractDetail: duplicatedContractDetail,
            hotel: hotelData,
            agentList: this.agentList,
            salePersonList: this.salePersonList,
            isDuplicate: true
          }
        })
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onPageChange: function (page) {
      this.currentPage = page
      this.initDataList()
    },
    onPreviewSaveDraft: async function () {
      this.isShowPreview = false
      const status = CONTRACT_STATUS.DRAFT
      await this._updateContractStatus(status)
    },
    onPreviewSavePublish: async function (contractId) {
      try {
        const status = CONTRACT_STATUS.PUBLISHED
        await this._updateContractStatus(status)

        this.contractList.map((item) => {
          if (item.id === contractId) {
            item.status = status
          }
        })
      } catch (err) {
        this.alertError(err.message)
      }
    },
    onOpenPopupSendEmailButtonClick: async function (contract) {
      const contractId = contract.contractId
      const fileType = contract.fileType

      try {
        this.startLoading()
        const result = await ContractApi.initEmail(contractId)
        this.contractDetail = result.contractDetail

        await this._prepareInitEmail(fileType)
        this.isPopupSendEmailVisible = true
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _prepareInitEmail: async function (fileType) {
      const contractHotelId = this.contractDetail.hotelId
      const contractId = this.contractDetail.id
      const hotelContract = this.hotelList.find(hotel => hotel.id === contractHotelId)
      const hotel = await getHotelInfo(hotelContract.id)

      const contactPersonEmailList = get(this.contractDetail, 'agent.contactPerson.emailList', [])

      const ccEmailList = get(hotel, 'emailConfiguration.ccEmailList', [])
      const bccEmailList = get(hotel, 'emailConfiguration.bccEmailList', [])

      const senderList = this._prepareSenderList()
      const attachment = await this._prepareAttachmentData(contractId, fileType)

      this.initEmailData = {
        hotelId: contractHotelId,
        senderList: senderList,
        toEmailList: contactPersonEmailList,
        ccEmailList: ccEmailList,
        bccEmailList: bccEmailList,
        attachmentList: [attachment]
      }
    },
    _prepareAttachmentData: async function (contractId, fileType) {
      await this._getContractDetail(contractId)
      await delay(1500)

      const data = prepareDocumentData(this.downloadContractDetail, fileType)
      const refCode = this.downloadContractDetail.refCode.toLowerCase()

      let documentType = DOCUMENT_TYPE
      let fileName = ''
      let documentResult = ''

      if (fileType === 'pdf') {
        documentType = 'pdf'
        fileName = getContractFileName(refCode, 'contract-', 'pdf')
        documentResult = await this._getContractPdfBase64Data(contractId, data, documentType, fileName)
      } else {
        documentType = DOCUMENT_TYPE
        fileName = getContractFileName(refCode)
        documentResult = await ContractApi.generateDocument(contractId, data, DOCUMENT_TYPE)
      }

      const attachment = {
        type: documentType,
        name: fileName,
        content: documentResult
      }

      return attachment
    },
    _getContractPdfBase64Data: async function (contractId, data, documentType, fileName, isDownload = false) {
      let documentResult = ''
      const imageData = await ContractApi.generateDocument(contractId, data, documentType)
      const html = this._prepareContractHtml(data, imageData)

      const opt = {
        margin: [0.3, 0.2],
        filename: fileName,
        image: { type: 'jpeg', quality: 1 },
        pagebreak: { avoid: '.avoid' },
        html2canvas: {
          scale: 2,
          dpi: 300,
          letterRendering: true
        },
        jsPDF: {
          unit: 'cm',
          format: 'A4',
          orientation: 'portrait'
        }
      }

      if (!isDownload) {
        const result = await html2Pdf().set(opt).from(html).outputPdf()
        const buff = Buffer.from(result, 'binary')

        documentResult = buff.toString('base64')
        return documentResult
      } else {
        await html2Pdf().set(opt).from(html).save()
        return true
      }
    },
    _prepareSenderList: function () {
      const senderList = this.hotelList.map((hotel) => {
        const hotelName = get(hotel, 'name', '')
        const emailSenderName = get(hotel, 'senderName', '')
        const replyEmail = get(hotel, 'replyEmail', '')
        const senderName = (emailSenderName === '') ? hotelName : emailSenderName

        const result = {
          hotelId: hotel.id,
          name: senderName,
          hotelName: hotelName,
          replyEmail: replyEmail
        }

        return result
      })

      senderList.sort((pre, next) => {
        return (pre.name > next.name) ? 1 : -1
      })

      return senderList
    },
    _updateContractStatus: async function (status) {
      try {
        this.startLoading()

        const id = this.downloadContractDetail.id
        await updateContractStatus(id, status)

        const title = this.$t('updateContractStatusSuccess')
        this.showNotificationSuccess(title)
        this._resetDownloadData(title)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onDownloadError: function (errorMessage) {
      this.alertError(errorMessage)
    },
    _getContractDetail: async function (contractId) {
      const result = await ContractApi.detail(contractId)
      this.downloadContractDetail = result.contractDetail
      this.downloadSupplementTypeList = result.supplementTypeList
      this.downloadCompanyData = result.companyData
      this.downloadHotelData = this.hotelList.find(hotel => hotel.id === this.downloadContractDetail.hotelId)
    },
    _prepareContractHtml: function (data, imageData) {
      const body = this._prepareContractBodyHtml(data, imageData)
      const footer = this._prepareContractFooterHtml(data, imageData)

      return `${body}${footer}`
    },
    _prepareContractBodyHtml: function (body, imageData) {
      const companyLogoBase64 = get(imageData, 'companyLogoBase64', '')
      const hotelLogoBase64 = get(imageData, 'hotelLogoBase64', '')

      const result = `
        <div id="pageContent" style="margin: 20px">
          <style>
            .font-pdf {
              font-size: 12px !important;
            }
          </style>
          <div class="font-pdf">
            <div>
              <table width="100%">
                  <tr>
                      <td width="100%" align="center">
                          <p><img data-v-742226fc="" width="90px" alt="Contract Manager logo" src="data:image/png;base64,${companyLogoBase64}" id="company-logo" crossorigin="*">
                          </p>
                      </td>
                  </tr>
              </table>
            </div>
            <table width="100%" style="margin-bottom: 20px;">
                <tr>
                    <td width="80%" id="contract-header">${body.header}</td>
                    <td width="20%" valign="middle">
                        <img width="90px" alt="Contract Manager logo" id="hotel-logo" src="data:image/png;base64,${hotelLogoBase64}" crossorigin="*">
                    </td>
                </tr>
            </table>
            ${body.detail}
            ${body.termsAndCondition}
          </div>
        </div>
      `

      return result
    },
    _prepareContractFooterHtml: function (body, imageData) {
      const signatureBase64 = get(imageData, 'signatureBase64', '')
      const contractDate = this.bindContractDate()
      let salePersonHtml = `<p style="margin-bottom: 130px"></p>`

      if (signatureBase64 !== '') {
        salePersonHtml = `
            <p style="text-align: center; margin-bottom: 10px">
                <img id="sale-person-signature" height="100px" alt="Signature" src="data:image/png;base64,${signatureBase64}" crossorigin="*">
            </p>
          `
      }

      const result = `
        <div id="pageFooter" class="avoid">
            <style>
              .font-pdf {
                font-size: 12px !important;
              }
            </style>
            <br>
            <div class="font-pdf">
                <table width="100%" class="avoid">
                    <tr>
                        <td width="5%">&nbsp;</td>
                        <td width="45%" style="padding-right: 80px; vertical-align: top;">
                            <p style="margin-bottom: 130px;">
                                <strong>Signed on behalf of the tour operator:</strong>
                            </p>
                            <hr style="color: rgb(0, 0, 0);">
                            <p>AUTHORIZED SIGNATURE </p>
                            <p>${body.contact.name}</p>
                            <p>${body.contact.position}</p>
                            <table width="100%">
                                <tr>
                                    <td style="vertical-align: bottom; height: 20px;">DATE:</td>
                                    <td width="90%" style="vertical-align: bottom; height: 20px; border-bottom: 1px solid;"></td>
                                </tr>
                            </table>
                        </td>
                        <td width="45%" style="padding-left: 80px; vertical-align: top;">
                            <p>
                                <strong>Signed on behalf of the Hotel:</strong>
                            </p>
                            ${salePersonHtml}
                            <hr style="color: rgb(0, 0, 0);">
                            <p>AUTHORIZED SIGNATURE</p>
                            <p>${body.salePerson.name}</p>
                            <p>${body.salePerson.position}</p>
                            <table width="100%">
                                <tr>
                                    <td style="vertical-align: bottom; height: 20px;">DATE:</td>
                                    <td width="90%" style="vertical-align: bottom; height: 20px;">${contractDate}</td>
                                </tr>
                            </table>
                        </td>
                        <td width="5%">&nbsp;</td>
                    </tr>
                </table>
            </div>
        </div>
      `

      return result
    },
    bindContractDate: function () {
      const updatedDateStr = get(this.downloadContractDetail, 'updatedAt', '')

      if (updatedDateStr !== '') {
        const updatedDate = new Date(updatedDateStr)
        return dateFormat.short(updatedDate, 'short', 'en')
      }

      return ''
    }
  }
}
</script>

<style scoped>
.hidden {
  display: none;
}
</style>
